import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sprout, 
  ArrowRight, 
  Sparkles, 
  Calendar, 
  Lock, 
  Zap, 
  Globe, 
  Droplets, 
  Scale, 
  Camera, 
  Clock,
  ShieldCheck
} from 'lucide-react';
import { api } from '../api/client';
import { User } from '../types';

interface HomePageProps {
  user: User | null;
  onOpenAuth: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ user, onOpenAuth }) => {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [recentSlugs, setRecentSlugs] = useState<Array<{ slug: string; title: string; category: string; lastVisited: number }>>([]);

  useEffect(() => {
    setRecentSlugs(api.getRecentSlugs());
  }, []);

  const handleOpenSlug = (targetSlug?: string) => {
    const raw = targetSlug || slug;
    if (!raw.trim()) return;
    const clean = raw.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '-').replace(/-+/g, '-');
    navigate(`/${clean}`);
  };

  const generateRandomSlug = () => {
    const adjectives = ['green', 'indoor', 'sunny', 'hydro', 'organic', 'wild', 'craft', 'turbo', 'secret'];
    const nouns = ['monstera', 'peppers', 'tomatoes', 'orchids', 'bonsai', 'cannabis', 'workshop', 'studio'];
    const num = Math.floor(1000 + Math.random() * 9000);
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const random = `${adj}-${noun}-${num}`;
    setSlug(random);
  };

  return (
    <div className="main-container">
      {/* Hero Section */}
      <div className="hero-box">
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <span className="badge badge-emerald">
            <Sparkles size={12} />
            <span>PWA · Edge Database · Zero Login Required</span>
          </span>
        </div>

        <h1 className="hero-title">Watch This Work</h1>
        <p className="hero-desc">
          ntfy-style live work & plant journals. Track growth timelines, watering, light cycles, and yield harvests.
          Choose any URL to start instantly.
        </p>

        {/* ntfy-style Slug Input */}
        <div className="ntfy-input-card">
          <span className="ntfy-prefix">watchthis.work/</span>
          <input
            type="text"
            className="ntfy-input"
            placeholder="enter-any-topic-or-slug..."
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleOpenSlug()}
            autoFocus
          />
          <button
            className="btn btn-secondary btn-sm"
            onClick={generateRandomSlug}
            title="Generate random topic"
            type="button"
          >
            <Sparkles size={14} />
            <span>Random</span>
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenSlug()}
            type="button"
          >
            <span>Open Log</span>
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Quick Topic Suggestions */}
        <div className="topic-pills">
          <span style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>Try an example:</span>
          {[
            'indoor-monstera-8219',
            'cherry-tomatoes-105',
            'bonsai-ficus-781',
            'hydro-chili-404',
            'woodshop-desk-92',
          ].map((topic) => (
            <button
              key={topic}
              className="topic-pill"
              onClick={() => handleOpenSlug(topic)}
            >
              /{topic}
            </button>
          ))}
        </div>
      </div>

      {/* Recent / Saved Topics (if any) */}
      {recentSlugs.length > 0 && (
        <div className="glass-panel" style={{ padding: '1.5rem', marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} color="var(--accent-emerald)" />
              <span>Your Bookmarked Logs</span>
            </h3>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>Stored on device</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
            {recentSlugs.map((item) => (
              <div
                key={item.slug}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s',
                }}
                onClick={() => navigate(`/${item.slug}`)}
                className="hover-glow"
              >
                <div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700 }}>{item.title}</h4>
                  <div style={{ fontSize: '0.78rem', color: 'var(--accent-emerald-light)' }}>
                    /{item.slug}
                  </div>
                </div>
                <ArrowRight size={16} color="var(--text-sub)" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feature Highlights Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        <div className="glass-panel" style={{ padding: '1.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <Globe size={22} color="var(--accent-emerald-light)" />
          </div>
          <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Simple Obfuscation Security</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Just like <strong>ntfy.sh</strong>, pick an unguessable URL e.g. <code style={{ color: 'var(--accent-emerald-light)' }}>/my-secret-grow-9412</code>.
            Instant access from any device without signup hurdles or friction.
          </p>
        </div>

        <div className="glass-panel" style={{ padding: '1.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(245, 158, 11, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <Calendar size={22} color="var(--accent-amber)" />
          </div>
          <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Calendar & Time-Based Views</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Switch effortlessly between monthly calendar schedules, chronological log feeds, and growth streak analytics.
          </p>
        </div>

        <div className="glass-panel" style={{ padding: '1.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(14, 165, 233, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <Sprout size={22} color="#38bdf8" />
          </div>
          <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Grower Metrics & Custom Fields</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Dedicated tracking for photoperiod light cycles (18/6, 12/12), vegetative days, feeding schedules, temperature, humidity, and harvest yields.
          </p>
        </div>

        <div className="glass-panel" style={{ padding: '1.75rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(139, 92, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <Lock size={22} color="var(--accent-purple)" />
          </div>
          <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Pro VIP: Private Google Auth</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Need absolute confidentiality? Sign in with Google to lock private jobs, backup high-resolution 4K media to Cloudflare R2, and customize domain routing.
          </p>
        </div>
      </div>
    </div>
  );
};
